/**
 * BETA — vente (sale) lifecycle handlers.
 */
import { httpError } from "../router.js";
import { paginate, matchSearch, newId, nowIso, recomputeVenteTotals, fefoDeduct, restoreStock } from "./_utils.js";

export function register(route) {
  route("GET", "/ventes", (ctx) => {
    const { params, store } = ctx;
    let rows = [...store.data.ventes];
    if (params.statut) rows = rows.filter((v) => v.statut === params.statut);
    if (params.type_vente) rows = rows.filter((v) => v.type_vente === params.type_vente);
    if (params.caissier_id) rows = rows.filter((v) => v.caissier_id === params.caissier_id);
    if (params.date_debut) rows = rows.filter((v) => v.date_vente >= params.date_debut);
    if (params.date_fin) rows = rows.filter((v) => v.date_vente <= params.date_fin + "T23:59:59.999Z");
    if (params.search) {
      rows = rows.filter((v) =>
        matchSearch(v, params.search, ["numero_interne"]) ||
        matchSearch(v.client || {}, params.search, ["nom", "prenom", "telephone"]),
      );
    }
    const sortBy = params.sort_by || "date_vente";
    const dir = params.sort_order === "asc" ? 1 : -1;
    rows.sort((x, y) => (x[sortBy] > y[sortBy] ? dir : -dir));
    return paginate(rows, params);
  });

  route("GET", "/ventes/:id", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    return v;
  });

  route("POST", "/ventes/libre", (ctx) => createVente(ctx, "LIBRE"));
  route("POST", "/ventes/chifa", (ctx) => createVente(ctx, "CHIFA"));

  route("PATCH", "/ventes/:id", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    if (v.statut !== "EN_COURS") httpError(400, "Vente non modifiable", "NOT_DRAFT");
    Object.assign(v, ctx.body || {});
    recomputeVenteTotals(v);
    ctx.commit();
    return v;
  });

  route("POST", "/ventes/:id/validate", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    if (v.statut !== "EN_COURS") httpError(400, "Déjà validée", "NOT_DRAFT");
    for (const d of v.details || []) {
      fefoDeduct(ctx.store, d.produit_id, d.quantite);
    }
    v.statut = "VALIDEE";
    v.validee_at = nowIso();
    v.updated_at = nowIso();
    ctx.commit();
    return v;
  });

  route("POST", "/ventes/:id/cancel", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    if (v.statut !== "VALIDEE") httpError(400, "Seules les ventes validées peuvent être annulées", "NOT_VALID");
    for (const d of v.details || []) {
      restoreStock(ctx.store, d.produit_id, d.quantite);
    }
    v.statut = "ANNULEE";
    v.motif_annulation = ctx.body?.motif || "";
    v.updated_at = nowIso();
    ctx.commit();
    return v;
  });

  route("DELETE", "/ventes/:id", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    if (v.statut !== "EN_COURS") httpError(400, "Seules les ventes en cours peuvent être supprimées", "NOT_DRAFT");
    ctx.store.data.ventes = ctx.store.data.ventes.filter((x) => x.id !== v.id);
    ctx.commit();
    return { success: true };
  });

  route("PATCH", "/ventes/:id/details/:detailId", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    const d = v.details.find((x) => x.id === ctx.params.detailId);
    if (!d) httpError(404, "Ligne introuvable", "NOT_FOUND");
    Object.assign(d, ctx.body || {});
    recomputeVenteTotals(v);
    ctx.commit();
    return v;
  });

  route("DELETE", "/ventes/:id/details/:detailId", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    v.details = v.details.filter((x) => x.id !== ctx.params.detailId);
    recomputeVenteTotals(v);
    ctx.commit();
    return v;
  });

  // ── Posologie γ sheets (low-literacy medication schedule) ────────────────
  // GET returns the posologie_data attached to each detail line of a vente.
  route("GET", "/ventes/:id/posologies", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    return (v.details || [])
      .filter((d) => d.posologie_data)
      .map((d) => ({ detail_id: d.id, ...d.posologie_data }));
  });

  // POST saves/updates one detail line's posologie sheet (cashier fills it
  // at the counter after validating the sale).
  route("POST", "/ventes/:id/posologies", (ctx) => {
    const v = ctx.store.data.ventes.find((x) => x.id === ctx.params.id);
    if (!v) httpError(404, "Vente introuvable", "NOT_FOUND");
    const body = ctx.body || {};
    const detailId = body.detail_id;
    const d = (v.details || []).find((x) => x.id === detailId);
    if (!d) httpError(404, "Ligne de vente introuvable", "NOT_FOUND_DETAIL");
    // Strip detail_id (already used above) — keep the rest as the posologie patch.
    const { detail_id: _detail_id, ...data } = body;
    d.posologie_data = {
      id: d.posologie_data?.id || newId("poso"),
      detail_vente_id: d.id,
      matin: data.matin ?? null,
      midi: data.midi ?? null,
      soir: data.soir ?? null,
      nuit: data.nuit ?? null,
      unite: data.unite ?? "cp",
      food_relation: data.food_relation ?? "INDIFFERENT",
      duration_days: data.duration_days ?? null,
      note: data.note ?? null,
      nom_ar: data.nom_ar ?? null,
      lang: data.lang ?? "fr",
      updated_at: nowIso(),
    };
    ctx.commit();
    return { detail_id: d.id, ...d.posologie_data };
  });
}

function createVente(ctx, type) {
  const body = ctx.body || {};
  const id = newId("vente-new");
  const num = `V-2026-${id.slice(-5).toUpperCase()}`;
  const client = body.client_id
    ? ctx.store.data.clients.find((c) => c.id === body.client_id)
    : null;
  const caissier =
    ctx.store.data.users.find((u) => u.role === "CAISSIER") ||
    ctx.store.data.users[0];

  const details = (body.details || []).map((it) => {
    const p = ctx.store.data.produits.find((x) => x.id === it.produit_id);
    return {
      id: newId("vente-det"),
      produit_id: it.produit_id,
      produit: p ? { id: p.id, nom_commercial: p.nom_commercial, ppa: p.ppa, dci: p.dci, forme_pharma: p.forme_pharma, dosage: p.dosage } : null,
      stock_lot_id: it.stock_lot_id || null,
      quantite: Number(it.quantite) || 1,
      prix_unitaire: Number(it.prix_unitaire ?? p?.ppa) || 0,
      remise_pct: Number(it.remise_pct) || 0,
      tva_pct: Number(it.tva_pct ?? p?.tva_pct) || 0,
      montant_ligne_ttc: 0,
      montant_pec_chifa: 0,
      montant_patient: 0,
    };
  });

  const v = {
    id,
    numero_interne: num,
    type_vente: type,
    statut: "EN_COURS",
    date_vente: nowIso(),
    client_id: client?.id ?? null,
    client: client ? { id: client.id, nom: client.nom, prenom: client.prenom, telephone: client.telephone, carte_chifa: client.carte_chifa } : null,
    caissier_id: caissier?.id ?? null,
    caissier: caissier ? { id: caissier.id, nom: caissier.nom, prenom: caissier.prenom } : null,
    caisse_id: body.caisse_id || "caisse-001",
    remise_montant: 0,
    remise_pct: 0,
    montant_total_ht: 0,
    montant_total_tva: 0,
    montant_total_ttc: 0,
    montant_pec_chifa: 0,
    montant_a_payer: 0,
    mode_paiement: body.mode_paiement || "ESPECES",
    notes: body.notes || "",
    details,
    carte_chifa_retenue: false,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  recomputeVenteTotals(v);
  ctx.store.data.ventes.unshift(v);
  ctx.commit();
  return v;
}
